import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import AppShell from '../components/AppShell';

const TYPE_LABELS = {
  buy_x_get_y: '🎁 Купи X отримай Y у подарунок',
  nth_percent: '🏷 Купи X — на наступні Y знижка %',
  percent_discount: '% Знижка відсотками',
  fixed_discount: '₴ Фіксована знижка',
};
const SCOPE_LABELS = {
  product: '📦 Товар',
  category: '📂 Категорія',
  cart: '🛒 Весь кошик',
};

const emptyForm = {
  name: '', type: 'percent_discount', scope: 'product',
  scope_id: '', buy_quantity: '1', get_quantity: '1',
  discount_value: '10', valid_from: '', valid_to: '',
};

export default function PromotionsPage() {
  const { id: storeId } = useParams();
  const navigate = useNavigate();
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadAll(); }, [storeId]);

  async function loadAll() {
    setLoading(true);
    const [promoRes, prodRes, catRes] = await Promise.all([
      api.get(`/promotions/stores/${storeId}`).catch(() => ({ data: [] })),
      api.get(`/catalog/stores/${storeId}/products`).catch(() => ({ data: [] })),
      api.get(`/catalog/stores/${storeId}/categories`).catch(() => ({ data: [] })),
    ]);
    setPromotions(promoRes.data);
    setProducts(prodRes.data.filter(p => !p.is_container));
    setCategories(catRes.data);
    setLoading(false);
  }

  // Локальний час із <input datetime-local> → UTC ISO (щоб 09:50 по Києву не їхало на годинник сервера)
  const toUtc = (v) => v ? new Date(v).toISOString().replace(/\.\d{3}Z$/, 'Z') : null;
  // UTC ISO з бази → значення для <input datetime-local> (локальний час)
  const toLocalInput = (iso) => {
    if (!iso) return '';
    const d = new Date(iso), p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  };

  function openCreate() {
    setForm(emptyForm); setEditingId(null); setError(''); setShowForm(true);
  }

  function startEdit(promo) {
    setForm({
      name: promo.name || '',
      type: promo.type,
      scope: promo.scope,
      scope_id: promo.scope_id || '',
      buy_quantity: String(promo.buy_quantity ?? '1'),
      get_quantity: String(promo.get_quantity ?? '1'),
      discount_value: String(promo.discount_value ?? '0'),
      valid_from: toLocalInput(promo.valid_from),
      valid_to: toLocalInput(promo.valid_to),
    });
    setEditingId(promo.id); setError(''); setShowForm(true);
  }

  function cancelForm() {
    setShowForm(false); setForm(emptyForm); setEditingId(null);
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError('');
    const payload = {
      ...form,
      scope_id: form.scope === 'cart' ? null : form.scope_id || null,
      buy_quantity: parseFloat(form.buy_quantity),
      get_quantity: parseFloat(form.get_quantity),
      discount_value: parseFloat(form.discount_value),
      valid_from: toUtc(form.valid_from),
      valid_to: toUtc(form.valid_to),
    };
    try {
      if (editingId) await api.patch(`/promotions/${editingId}`, payload);
      else await api.post(`/promotions/stores/${storeId}`, payload);
      await loadAll();
      cancelForm();
    } catch(err) { setError(err.response?.data?.detail || 'Помилка'); }
  }

  async function toggleActive(promo) {
    await api.patch(`/promotions/${promo.id}`, { is_active: !promo.is_active });
    await loadAll();
  }

  async function deletePromo(id) {
    if (!window.confirm('Видалити акцію?')) return;
    await api.delete(`/promotions/${id}`);
    await loadAll();
  }

  function getScopeName(promo) {
    if (promo.scope === 'cart') return '🛒 Весь кошик';
    if (promo.scope === 'product') {
      const p = products.find(p => p.id === promo.scope_id);
      return p ? `📦 ${p.name}` : '📦 ?';
    }
    if (promo.scope === 'category') {
      const c = categories.find(c => c.id === promo.scope_id);
      return c ? `📂 ${c.name}` : '📂 ?';
    }
    return '?';
  }

  function getPromoDescription(promo) {
    if (promo.type === 'buy_x_get_y') {
      return `Купи ${promo.buy_quantity} → отримай ${promo.get_quantity} безкоштовно`;
    }
    if (promo.type === 'nth_percent') {
      return `Купи ${promo.buy_quantity} → на наступні ${promo.get_quantity} знижка ${promo.discount_value}%`;
    }
    if (promo.type === 'percent_discount') {
      return `Знижка ${promo.discount_value}%`;
    }
    if (promo.type === 'fixed_discount') {
      return `Знижка ${promo.discount_value} ₴`;
    }
    return '';
  }

  function isExpired(promo) {
    if (!promo.valid_to) return false;
    return new Date(promo.valid_to) < new Date();
  }

  function isNotStarted(promo) {
    if (!promo.valid_from) return false;
    return new Date(promo.valid_from) > new Date();
  }

  const active = promotions.filter(p => p.is_active && !isExpired(p) && !isNotStarted(p));
  const inactive = promotions.filter(p => !p.is_active || isExpired(p) || isNotStarted(p));

  return (
    <AppShell
      title="🎯 Акції та знижки"
      onBack={() => navigate(`/store/${storeId}`)}
    >
      <div className="row" style={{ justifyContent:'flex-end', marginBottom:18 }}>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>
          + Створити акцію
        </button>
      </div>

      <div>
        {/* Форма створення */}
        {showForm && (
          <div style={styles.formCard}>
            <h3 style={styles.formTitle}>{editingId ? '✏️ Редагувати акцію' : 'Нова акція'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Назва акції</label>
                  <input style={styles.input} placeholder="Наприклад: Щаслива година"
                    value={form.name} onChange={e => setForm(f=>({...f,name:e.target.value}))} required />
                </div>
              </div>

              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Тип акції</label>
                  <select style={styles.input} value={form.type}
                    onChange={e => setForm(f=>({...f,type:e.target.value}))}>
                    {Object.entries(TYPE_LABELS).map(([k,v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Застосувати до</label>
                  <select style={styles.input} value={form.scope}
                    onChange={e => setForm(f=>({...f,scope:e.target.value,scope_id:''}))}>
                    {Object.entries(SCOPE_LABELS).map(([k,v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Вибір товару або категорії */}
              {form.scope === 'product' && (
                <div style={styles.field}>
                  <label style={styles.label}>Товар</label>
                  <select style={styles.input} value={form.scope_id}
                    onChange={e => setForm(f=>({...f,scope_id:e.target.value}))} required>
                    <option value="">Оберіть товар...</option>
                    {products.map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {form.scope === 'category' && (
                <div style={styles.field}>
                  <label style={styles.label}>Категорія</label>
                  <select style={styles.input} value={form.scope_id}
                    onChange={e => setForm(f=>({...f,scope_id:e.target.value}))} required>
                    <option value="">Оберіть категорію...</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.photo_url} {c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Параметри акції */}
              {form.type === 'buy_x_get_y' && (
                <>
                  <div style={styles.row}>
                    <div style={styles.field}>
                      <label style={styles.label}>Купи (к-ть од.)</label>
                      <input style={styles.input} type="number" step="0.5" min="1"
                        value={form.buy_quantity}
                        onChange={e => setForm(f=>({...f,buy_quantity:e.target.value}))} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Отримай безкоштовно (к-ть)</label>
                      <input style={styles.input} type="number" step="0.5" min="0.5"
                        value={form.get_quantity}
                        onChange={e => setForm(f=>({...f,get_quantity:e.target.value}))} />
                    </div>
                  </div>
                  <div style={styles.promoHint}>
                    🎁 На кожні <b>{form.buy_quantity || '?'}</b> од. пробитих — <b>{form.get_quantity || '?'}</b> у подарунок (по 0.01 ₴).
                    {parseFloat(form.buy_quantity) > 0 && parseFloat(form.get_quantity) > 0 &&
                      ` Напр.: касир пробив ${form.buy_quantity} — додасться ${form.get_quantity} безкоштовно.`}
                    <br/><span style={styles.promoHintDim}>Увага: це «купи N — отримай ще M зверху», а не «M з N за копійку».</span>
                  </div>
                </>
              )}
              {form.type === 'nth_percent' && (
                <>
                  <div style={styles.row}>
                    <div style={styles.field}>
                      <label style={styles.label}>Купи (повних од.)</label>
                      <input style={styles.input} type="number" step="0.5" min="1"
                        value={form.buy_quantity}
                        onChange={e => setForm(f=>({...f,buy_quantity:e.target.value}))} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Наступних зі знижкою (к-ть)</label>
                      <input style={styles.input} type="number" step="0.5" min="0.5"
                        value={form.get_quantity}
                        onChange={e => setForm(f=>({...f,get_quantity:e.target.value}))} />
                    </div>
                    <div style={styles.field}>
                      <label style={styles.label}>Знижка на них (%)</label>
                      <input style={styles.input} type="number" step="1" min="1" max="100"
                        value={form.discount_value}
                        onChange={e => setForm(f=>({...f,discount_value:e.target.value}))} />
                    </div>
                  </div>
                  <div style={styles.promoHint}>
                    🏷 На кожні <b>{form.buy_quantity || '?'}</b> повних — наступні <b>{form.get_quantity || '?'}</b> зі знижкою <b>{form.discount_value || '?'}%</b>.
                    {parseFloat(form.buy_quantity) > 0 && parseFloat(form.get_quantity) > 0 &&
                      ` Напр.: ${form.buy_quantity} л повна, ще ${form.get_quantity} л — за -${form.discount_value || '?'}%.`}
                  </div>
                </>
              )}
              {(form.type === 'percent_discount' || form.type === 'fixed_discount') && (
                <div style={styles.row}>
                  <div style={styles.field}>
                    <label style={styles.label}>
                      {form.type === 'percent_discount' ? 'Знижка (%)' : 'Знижка (₴)'}
                    </label>
                    <input style={styles.input} type="number" step="0.01" min="0"
                      max={form.type === 'percent_discount' ? '100' : undefined}
                      value={form.discount_value}
                      onChange={e => setForm(f=>({...f,discount_value:e.target.value}))} />
                  </div>
                </div>
              )}

              {/* Дати */}
              <div style={styles.row}>
                <div style={styles.field}>
                  <label style={styles.label}>Діє з (необов'язково)</label>
                  <input style={styles.input} type="datetime-local"
                    value={form.valid_from}
                    onChange={e => setForm(f=>({...f,valid_from:e.target.value}))} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Діє до (необов'язково)</label>
                  <input style={styles.input} type="datetime-local"
                    value={form.valid_to}
                    onChange={e => setForm(f=>({...f,valid_to:e.target.value}))} />
                </div>
              </div>

              {error && <p style={styles.error}>{error}</p>}
              <div style={styles.formBtns}>
                <button type="submit" style={styles.saveBtn}>{editingId ? 'Зберегти зміни' : 'Створити акцію'}</button>
                <button type="button" style={styles.cancelBtn}
                  onClick={cancelForm}>Скасувати</button>
              </div>
            </form>
          </div>
        )}

        {/* Активні акції */}
        {loading ? <p style={styles.hint}>Завантаження...</p> : (
          <>
            {active.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>🟢 Активні акції ({active.length})</h3>
                <div style={styles.promoList}>
                  {active.map(promo => (
                    <PromoCard key={promo.id} promo={promo}
                      scopeName={getScopeName(promo)}
                      description={getPromoDescription(promo)}
                      onEdit={() => startEdit(promo)}
                      onToggle={() => toggleActive(promo)}
                      onDelete={() => deletePromo(promo.id)} />
                  ))}
                </div>
              </div>
            )}

            {inactive.length > 0 && (
              <div style={styles.section}>
                <h3 style={styles.sectionTitle}>⚫ Неактивні / завершені ({inactive.length})</h3>
                <div style={styles.promoList}>
                  {inactive.map(promo => (
                    <PromoCard key={promo.id} promo={promo}
                      scopeName={getScopeName(promo)}
                      description={getPromoDescription(promo)}
                      expired={isExpired(promo)}
                      onEdit={() => startEdit(promo)}
                      onToggle={() => toggleActive(promo)}
                      onDelete={() => deletePromo(promo.id)} />
                  ))}
                </div>
              </div>
            )}

            {promotions.length === 0 && (
              <div style={styles.empty}>
                <div style={{fontSize:'64px', marginBottom:'16px'}}>🎯</div>
                <p>Акцій ще немає — створіть першу!</p>
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}

function PromoCard({ promo, scopeName, description, expired, onEdit, onToggle, onDelete }) {
  const formatDate = iso => iso ? new Date(iso).toLocaleDateString('uk-UA') : null;

  return (
    <div style={{...cardStyles.wrap, ...(promo.is_active && !expired ? {} : cardStyles.wrapInactive)}}>
      <div style={cardStyles.top}>
        <div style={cardStyles.info}>
          <div style={cardStyles.name}>{promo.name}</div>
          <div style={cardStyles.desc}>{description}</div>
          <div style={cardStyles.meta}>
            <span style={cardStyles.scope}>{scopeName}</span>
            {promo.valid_from && <span style={cardStyles.date}>з {formatDate(promo.valid_from)}</span>}
            {promo.valid_to && <span style={cardStyles.date}>до {formatDate(promo.valid_to)}</span>}
            {expired && <span style={cardStyles.expired}>⏰ Завершена</span>}
          </div>
        </div>
        <div style={cardStyles.typeBadge}>
          {promo.type === 'buy_x_get_y' && '🎁'}
          {promo.type === 'nth_percent' && `-${promo.discount_value}%`}
          {promo.type === 'percent_discount' && `${promo.discount_value}%`}
          {promo.type === 'fixed_discount' && `${promo.discount_value}₴`}
        </div>
      </div>
      <div style={cardStyles.actions}>
        <button style={cardStyles.editBtn} onClick={onEdit}>✏️ Редагувати</button>
        <button style={promo.is_active ? cardStyles.deactivateBtn : cardStyles.activateBtn}
          onClick={onToggle}>
          {promo.is_active ? '⏸ Деактивувати' : '▶ Активувати'}
        </button>
        <button style={cardStyles.deleteBtn} onClick={onDelete}>🗑</button>
      </div>
    </div>
  );
}

const cardStyles = {
  wrap: { background:'#211c14', border:'1px solid #2d6a4f', borderRadius:'12px', padding:'16px', marginBottom:'8px' },
  wrapInactive: { border:'1px solid #38301f', opacity:0.7 },
  top: { display:'flex', gap:'12px', marginBottom:'10px' },
  info: { flex:1 },
  name: { fontWeight:'bold', fontSize:'15px', marginBottom:'4px' },
  desc: { color:'#6cb6ff', fontSize:'13px', marginBottom:'6px' },
  meta: { display:'flex', gap:'8px', flexWrap:'wrap' },
  scope: { background:'#38301f', color:'#9a8a6e', padding:'2px 8px', borderRadius:'10px', fontSize:'11px' },
  date: { color:'#9a8a6e', fontSize:'11px' },
  expired: { color:'#ff6b6b', fontSize:'11px' },
  typeBadge: { background:'#1c1812', color:'#f5a623', borderRadius:'10px', padding:'6px 14px', fontWeight:'bold', fontSize:'16px', height:'fit-content', whiteSpace:'nowrap' },
  actions: { display:'flex', gap:'8px', flexWrap:'wrap' },
  editBtn: { padding:'6px 14px', background:'#1c2738', color:'#6cb6ff', border:'1px solid #2a3a52', borderRadius:'7px', cursor:'pointer', fontSize:'12px' },
  activateBtn: { padding:'6px 14px', background:'#2d6a4f', color:'#5fbf8f', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px' },
  deactivateBtn: { padding:'6px 14px', background:'#38301f', color:'#9a8a6e', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'12px' },
  deleteBtn: { padding:'6px 10px', background:'rgba(255,107,107,.18)', color:'#ff6b6b', border:'none', borderRadius:'7px', cursor:'pointer', fontSize:'13px' },
};

const styles = {
  formCard: { background:'#211c14', border:'1px solid #38301f', borderRadius:'12px', padding:'24px', marginBottom:'24px' },
  formTitle: { color:'#f5a623', margin:'0 0 20px' },
  row: { display:'flex', gap:'16px', marginBottom:'14px', flexWrap:'wrap' },
  field: { flex:1, minWidth:'200px' },
  label: { display:'block', color:'#9a8a6e', fontSize:'11px', marginBottom:'5px', textTransform:'uppercase' },
  input: { width:'100%', padding:'10px 14px', borderRadius:'8px', border:'1px solid #38301f', background:'#1c1812', color:'#fff', fontSize:'14px', boxSizing:'border-box' },
  promoHint: { background:'rgba(95,191,143,.10)', border:'1px solid rgba(95,191,143,.3)', color:'#9fe3c0', borderRadius:'10px', padding:'10px 14px', fontSize:'13px', marginBottom:'14px', lineHeight:'1.5' },
  promoHintDim: { color:'#9a8a6e', fontSize:'12px' },
  error: { color:'#ff6b6b', fontSize:'13px', marginBottom:'10px' },
  formBtns: { display:'flex', gap:'10px' },
  saveBtn: { padding:'10px 24px', background:'#f5a623', color:'#14110c', border:'none', borderRadius:'8px', fontWeight:'bold', cursor:'pointer' },
  cancelBtn: { padding:'10px 16px', background:'transparent', color:'#9a8a6e', border:'1px solid #38301f', borderRadius:'8px', cursor:'pointer' },
  section: { marginBottom:'24px' },
  sectionTitle: { color:'#f5a623', marginBottom:'12px', fontSize:'15px' },
  promoList: {},
  empty: { textAlign:'center', color:'#9a8a6e', padding:'40px' },
  hint: { color:'#9a8a6e', textAlign:'center', padding:'20px' },
};
